import { Link } from 'react-router-dom';
import { MapPin, Star } from 'lucide-react';
import type { JobOffer } from '../../types/offres';
import { ContractBadge, RemoteBadge } from './OfferBadge';

interface OfferCardProps {
  offer: JobOffer;
}

export function OfferCard({ offer }: OfferCardProps) {
  const publishedDate = offer.published_at
    ? new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(offer.published_at))
    : null;

  return (
    <Link
      to={`/offres/${offer.slug}`}
      className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md"
    >
      {offer.featured && (
        <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
          <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
          Mise en avant
        </div>
      )}

      <div className="flex items-start gap-4">
        {offer.company_logo_url ? (
          <img
            src={offer.company_logo_url}
            alt={offer.company_name ?? offer.title}
            className="h-12 w-12 rounded-xl border border-slate-100 object-contain p-1"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-100 bg-slate-50 text-lg font-bold text-slate-400">
            {offer.title.charAt(0).toUpperCase()}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-slate-500">
            {offer.company_name ?? 'recruteurs.io'}
          </p>
          <h2 className="mt-0.5 truncate text-base font-semibold text-slate-900 group-hover:text-pink-600 transition-colors">
            {offer.title}
          </h2>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <ContractBadge type={offer.contract_type} />
        <RemoteBadge type={offer.remote_type} />
        {offer.sector && (
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600">
            {offer.sector}
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
        <span className="flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          {offer.location_city}
        </span>
        {offer.salary_range && (
          <span className="font-medium text-slate-700">{offer.salary_range}</span>
        )}
      </div>

      {publishedDate && (
        <p className="mt-4 border-t border-slate-100 pt-4 text-xs text-slate-400">
          Publiée le {publishedDate}
        </p>
      )}
    </Link>
  );
}
